/**
 * lib/beat-pattern.ts — whether the next track's beats go on as they are or half a beat on.
 *
 * Task 46, 2026-10-05, the owner's choice; the mix of task 47 calls it. The loader keeps each
 * track's drum pattern of one beat (`beat.profile`, `apps/api/src/routes/catalogue.ts`): eight
 * marks — eighths of the beat — of the low bands (the kick, the bass), then eight of the high
 * (the snare, the hats). It cannot tell which of two eighths is the beat: in dancehall the
 * loudest hit is now on the beat, now half a beat on, and the owner's ear could not tell either
 * (`.omc/wiki/ritm-na-nastoyashchih-trekah.md`). So the mix does not ask which eighth is «one»: it
 * lays the next track so that its pattern matches the playing one's — kick on kick, snare on snare.
 *
 * ## Only what tells the beat from the half-beat
 *
 * A pattern compared whole says little: most of it is the same turned half a beat on — the
 * channel's tracks are loud on both eighths, and the whole patterns of all 153 pairs of 18 tracks
 * matched as well either way, to half a hundredth. What differs is each mark minus the mark half a
 * beat on. The agreement of those differences between the two tracks, from −1 to 1, says which way
 * they match: near 1 — as they are; near −1 — half a beat on; in between — either. On the 18 tracks
 * it was at least {@link SURE_AGREEMENT} either way for 34 pairs of 153; for the rest the mix lays
 * the beats as the detector named them, and whether that is heard is checked by ear in task 52.
 */

/** Marks of one beat per band (`apps/loader/src/beats/detect.ts`, `PROFILE_MARKS`). */
export const PATTERN_MARKS = 8;

/** The agreement, either way, from which the patterns decide. Below it the beats stay as named. */
export const SURE_AGREEMENT = 0.5;

export interface HalfBeat {
  /** How far the next track's beats go on from where its first beat is named, in beats. */
  readonly shiftBeats: 0 | 0.5;
  /** The patterns decided it; `false` — they did not, and the beats stay as named. */
  readonly sure: boolean;
}

const AS_NAMED: HalfBeat = Object.freeze({ shiftBeats: 0, sure: false });

/** A pattern the loader could have written: two bands of eight finite marks. */
function usable(pattern: readonly number[] | null | undefined): pattern is readonly number[] {
  return (
    pattern !== null &&
    pattern !== undefined &&
    pattern.length === 2 * PATTERN_MARKS &&
    pattern.every((mark) => Number.isFinite(mark))
  );
}

/** Per band, the first four marks minus the four half a beat on: what tells beat from half-beat. */
function difference(pattern: readonly number[]): number[] {
  const out: number[] = [];
  for (let band = 0; band < 2; band += 1) {
    for (let mark = 0; mark < PATTERN_MARKS / 2; mark += 1) {
      const at = band * PATTERN_MARKS + mark;
      out.push((pattern[at] ?? 0) - (pattern[at + PATTERN_MARKS / 2] ?? 0));
    }
  }
  return out;
}

/**
 * Whether the next track (`next`) goes on as its beats are named or half a beat on, to match the
 * playing one (`playing`) — see the header.
 */
export function halfBeatShift(
  playing: readonly number[] | null | undefined,
  next: readonly number[] | null | undefined,
): HalfBeat {
  if (!usable(playing) || !usable(next)) return AS_NAMED;
  const a = difference(playing);
  const b = difference(next);
  let dot = 0;
  let aa = 0;
  let bb = 0;
  for (let i = 0; i < a.length; i += 1) {
    dot += (a[i] ?? 0) * (b[i] ?? 0);
    aa += (a[i] ?? 0) ** 2;
    bb += (b[i] ?? 0) ** 2;
  }
  if (aa === 0 || bb === 0) return AS_NAMED;
  const agreement = dot / Math.sqrt(aa * bb);
  if (agreement >= SURE_AGREEMENT) return { shiftBeats: 0, sure: true };
  if (agreement <= -SURE_AGREEMENT) return { shiftBeats: 0.5, sure: true };
  return AS_NAMED;
}
